import React, { useEffect, useImperativeHandle, useMemo, useState, forwardRef } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import {
  Alert,
  Button,
  Card,
  Empty,
  List,
  Space,
  Spin,
  Switch,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd'
import { arrayMove } from '@dnd-kit/sortable'
import { DragEndEvent } from '@dnd-kit/core'
import { translate, translateTemplate } from '../../../core'
import {
  DraggableList,
  DraggableListItem,
  DragListItemHandle,
} from '../../../core/component'
import { ItemType } from '../../inventory/type/item'
import {
  GetFilterBaseConfig,
  SaveFilterBaseConfig,
} from '../graphql/filter-config.graphql'
import {
  FilterBaseConfigEntryView,
  SaveBaseConfigEntryInput,
} from '../type/filter-config'
import {
  FilterKind,
  isFilterAllowedForType,
  isPhase4Disabled,
  productTypeDisplayLabel,
} from '../type/filter-catalog'

interface Row extends FilterBaseConfigEntryView {
  key: string
}

interface Props {
  productType: ItemType
  /** Notifies the parent when the panel has unsaved local edits. */
  onDirtyChange?: (dirty: boolean) => void
}

/** Parent grabs this ref to force-reset the panel after a confirmed discard. */
export interface FilterBaseConfigPanelHandle {
  reset: () => void
}

export const FilterBaseConfigPanel = forwardRef<FilterBaseConfigPanelHandle, Props>(
  function FilterBaseConfigPanel({ productType, onDirtyChange }, ref) {
  const [rows, setRows] = useState<Row[]>([])
  const [initialFingerprint, setInitialFingerprint] = useState<string>('')
  const [messageApi, contextHolder] = message.useMessage()

  const { data, loading, refetch } = useQuery(GetFilterBaseConfig, {
    variables: { productType },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
  })

  useEffect(() => {
    if (data?.filterBaseConfig) {
      const sorted = data.filterBaseConfig
        .filter((entry) => isFilterAllowedForType(entry.filterKey, productType))
        .map<Row>((entry) => ({ ...entry, key: entry.filterKey }))
        .sort(
          (a, b) =>
            Number(b.enabled) - Number(a.enabled) || a.displayOrder - b.displayOrder,
        )
      // Server displayOrder values may be non-contiguous (e.g. 0,1,3 after a
      // disable). Reflow on load so the dirty-check fingerprint compares the
      // same shape we produce on every local edit.
      const next = reflowDisplayOrder(sorted)
      setRows(next)
      setInitialFingerprint(fingerprintEnabled(next))
    }
  }, [data, productType])

  const [save, { loading: saving }] = useMutation(SaveFilterBaseConfig, {
    onCompleted: () => {
      messageApi.success(translate('base_config_save_success'))
      refetch()
    },
    onError: (err) => messageApi.error(err.message),
  })

  const dirty = useMemo(
    () => initialFingerprint !== fingerprintEnabled(rows),
    [rows, initialFingerprint],
  )

  useEffect(() => {
    onDirtyChange?.(dirty)
  }, [dirty, onDirtyChange])

  // Allow the parent to force a discard after a confirmation modal.
  useImperativeHandle(ref, () => ({
    reset: () => {
      if (!data?.filterBaseConfig) return
      const sorted = data.filterBaseConfig
        .filter((entry) => isFilterAllowedForType(entry.filterKey, productType))
        .map<Row>((entry) => ({ ...entry, key: entry.filterKey }))
        .sort(
          (a, b) =>
            Number(b.enabled) - Number(a.enabled) || a.displayOrder - b.displayOrder,
        )
      const next = reflowDisplayOrder(sorted)
      setRows(next)
      setInitialFingerprint(fingerprintEnabled(next))
    },
  }))

  const onToggle = (key: string, enabled: boolean) => {
    setRows((prev) => {
      const next = prev.map((r) => (r.key === key ? { ...r, enabled } : r))
      return reflowDisplayOrder(next)
    })
  }

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return
    setRows((prev) => {
      const enabledRows = prev.filter((r) => r.enabled)
      const disabledRows = prev.filter((r) => !r.enabled)
      const fromIdx = enabledRows.findIndex((r) => r.key === active.id)
      const toIdx = enabledRows.findIndex((r) => r.key === over.id)
      if (fromIdx < 0 || toIdx < 0) return prev
      const reordered = arrayMove(enabledRows, fromIdx, toIdx)
      return reflowDisplayOrder([...reordered, ...disabledRows])
    })
  }

  const onDiscard = () => {
    if (data?.filterBaseConfig) {
      const sorted = data.filterBaseConfig
        .filter((entry) => isFilterAllowedForType(entry.filterKey, productType))
        .map<Row>((entry) => ({ ...entry, key: entry.filterKey }))
        .sort(
          (a, b) =>
            Number(b.enabled) - Number(a.enabled) || a.displayOrder - b.displayOrder,
        )
      const next = reflowDisplayOrder(sorted)
      setRows(next)
      setInitialFingerprint(fingerprintEnabled(next))
    }
  }

  const enabledRows = rows.filter((r) => r.enabled)

  // Send the state of every row on screen, on and off, so the save changes
  // exactly what this panel shows and nothing it doesn't.
  const onSave = () => {
    const enabledFilters: SaveBaseConfigEntryInput[] = enabledRows.map((r, index) => ({
      filterKey: r.filterKey,
      displayOrder: index,
    }))
    const disabledFilters = rows.filter((r) => !r.enabled).map((r) => r.filterKey)
    save({ variables: { input: { productType, enabledFilters, disabledFilters } } })
  }

  return (
    <Spin spinning={loading && rows.length === 0}>
      {contextHolder}
      <Space
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12,
          flexWrap: 'wrap',
          gap: 8,
        }}
      >
        <Space>
          {dirty && (
            <Tag color="orange">{translate('unsaved_changes')}</Tag>
          )}
        </Space>
        <Space>
          <Button onClick={onDiscard} disabled={!dirty || saving}>
            {translate('discard')}
          </Button>
          <Button type="primary" loading={saving} disabled={!dirty} onClick={onSave}>
            {translate('save_all')}
          </Button>
        </Space>
      </Space>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message={translateTemplate('base_config_scope_hint', {
          type: productTypeDisplayLabel(productType),
        })}
      />

      {rows.length === 0 ? (
        <Empty />
      ) : (
        <DraggableList items={enabledRows} onDragEnd={onDragEnd}>
          <List
            dataSource={rows}
            renderItem={(row) => {
              const isSpecific = row.kind === FilterKind.Specific
              // Not rendered by the website yet: can't be switched on, but one
              // that is already on can still be switched off.
              const phase4Disabled = isPhase4Disabled(row.key)
              return (
                <DraggableListItem key={row.key} id={row.key}>
                  <Card
                    size="small"
                    style={{ marginBottom: 8 }}
                    bodyStyle={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                    }}
                  >
                    {row.enabled ? <DragListItemHandle /> : <div style={{ width: 32 }} />}
                    <Typography.Text strong style={{ flex: 1, fontSize: 15 }}>
                      {row.label}
                    </Typography.Text>
                    <Tag color={isSpecific ? 'purple' : 'blue'}>
                      {isSpecific ? translate('specific_filter') : translate('general_filter')}
                    </Tag>
                    {phase4Disabled && (
                      <Tooltip title={translate('filter_phase4_disabled')}>
                        <Tag color="gold" style={{ marginInlineEnd: 0, cursor: 'help' }}>
                          {translate('filter_phase4_short')}
                        </Tag>
                      </Tooltip>
                    )}
                    <Switch
                      checked={row.enabled}
                      disabled={phase4Disabled && !row.enabled}
                      onChange={(checked) => onToggle(row.key, checked)}
                    />
                  </Card>
                </DraggableListItem>
              )
            }}
          />
        </DraggableList>
      )}

      <Card size="small" style={{ marginTop: 16 }} title={translate('effective_preview')}>
        {enabledRows.length === 0 ? (
          <Alert
            type="info"
            showIcon
            message={translate('no_rules_configured')}
            style={{ marginTop: 0 }}
          />
        ) : (
          <Space wrap>
            {enabledRows.map((r) => (
              <Tag key={r.key} color="geekblue">
                {r.label}
              </Tag>
            ))}
          </Space>
        )}
      </Card>
    </Spin>
  )
})

function fingerprintEnabled(rows: Row[]): string {
  return rows
    .filter((r) => r.enabled)
    .map((r) => `${r.key}:${r.displayOrder}`)
    .join('|')
}

function reflowDisplayOrder(rows: Row[]): Row[] {
  let order = 0
  return rows.map((r) =>
    r.enabled ? { ...r, displayOrder: order++ } : { ...r, displayOrder: 0 },
  )
}
